import type { DocumentNode, QueryHookOptions, TypedDocumentNode } from '@apollo/client';

import type { ENTITY_TYPES } from 'bundles/unified-seo-content-common/constants';
import CourseEntityCardQuery from 'bundles/unified-seo-content-common/queries/CourseEntityCardQuery.graphql';
import getDegreesBySlug from 'bundles/unified-seo-content-common/queries/GetDegreesBySlug.graphql';
import SpecializationEntityCardQuery from 'bundles/unified-seo-content-common/queries/SpecializationEntityCardQuery.graphql';
import type {
  CourseEntityCardFieldsFragment,
  CourseEntityCardQuery as CourseEntityCardQueryType,
  CourseEntityCardQueryVariables,
} from 'bundles/unified-seo-content-common/queries/__generated__/CourseEntityCardQuery';
import type {
  WithDegreeMetadataPremiumProductCollections_LearningProductFragment as DegreeMetadataType,
  GetDegreesBySlugQuery as GetDegreesBySlugQueryData,
  GetDegreesBySlugQueryVariables,
} from 'bundles/unified-seo-content-common/queries/__generated__/GetDegreesBySlug';
import type {
  MasterTrackEntityQuery as MasterTrackEntityQueryData,
  MasterTrackEntityQueryVariables,
  MasterTrackEntityQuery_CatalogMastertracksV1Resource_bySlug_elements as MasterTrackMetadataType,
} from 'bundles/unified-seo-content-common/queries/__generated__/MasterTrackEntityQuery';
import type {
  SpecializationEntityCardFieldsFragment,
  SpecializationEntityCardQuery as SpecializationEntityCardQueryType,
  SpecializationEntityCardQueryVariables,
} from 'bundles/unified-seo-content-common/queries/__generated__/SpecializationEntityCardQuery';
import { MasterTrackEntityQuery } from 'bundles/unified-seo-content-common/queries/assemblerQueries';
import type { EntityType } from 'bundles/unified-seo-content-common/types';

type EntityTypesSuperMapping = {
  [ENTITY_TYPES.course]: {
    EntityQueryData: CourseEntityCardQueryType;
    EntityQueryVariables: CourseEntityCardQueryVariables;
    EntityQueryResultData: CourseEntityCardFieldsFragment;
  };
  [ENTITY_TYPES.project]: {
    EntityQueryData: CourseEntityCardQueryType;
    EntityQueryVariables: CourseEntityCardQueryVariables;
    EntityQueryResultData: CourseEntityCardFieldsFragment;
  };
  [ENTITY_TYPES.s12n]: {
    EntityQueryData: SpecializationEntityCardQueryType;
    EntityQueryVariables: SpecializationEntityCardQueryVariables;
    EntityQueryResultData: SpecializationEntityCardFieldsFragment;
  };
  [ENTITY_TYPES.profS12n]: {
    EntityQueryData: SpecializationEntityCardQueryType;
    EntityQueryVariables: SpecializationEntityCardQueryVariables;
    EntityQueryResultData: SpecializationEntityCardFieldsFragment;
  };
  [ENTITY_TYPES.degree]: {
    EntityQueryData: GetDegreesBySlugQueryData;
    EntityQueryVariables: GetDegreesBySlugQueryVariables;
    EntityQueryResultData: DegreeMetadataType;
  };
  [ENTITY_TYPES.mastertrack]: {
    EntityQueryData: MasterTrackEntityQueryData;
    EntityQueryVariables: MasterTrackEntityQueryVariables;
    EntityQueryResultData: MasterTrackMetadataType;
  };
};

export type EntityQueryData<T extends EntityType> = EntityTypesSuperMapping[T]['EntityQueryData'];
export type EntityQueryVariables<T extends EntityType> = EntityTypesSuperMapping[T]['EntityQueryVariables'];
export type EntityQueryResultData<T extends EntityType> = EntityTypesSuperMapping[T]['EntityQueryResultData'];

export type EntityQueryConfigs<T extends EntityType> = {
  query: DocumentNode | TypedDocumentNode<EntityQueryData<T>, EntityQueryVariables<T>>;
  queryOptions?: QueryHookOptions<EntityQueryData<T>, EntityQueryVariables<T>>;
  dataResolver: (data?: EntityQueryData<T>) => {
    data?: EntityQueryResultData<T>;
  };
};

export const getCourseQueryConfig = (slug: string): EntityQueryConfigs<'course'> => ({
  query: CourseEntityCardQuery,
  queryOptions: {
    skip: !slug,
    variables: { slug },
    context: { clientName: 'gatewayGql' },
  },
  dataResolver: (data) => {
    return {
      data: data?.DescriptionPage?.queryCoursePage ?? undefined,
    };
  },
});

export const getSpecializationQueryConfig = (slug: string): EntityQueryConfigs<'s12n'> => ({
  query: SpecializationEntityCardQuery,
  queryOptions: {
    skip: !slug,
    variables: { slug },
    context: { clientName: 'gatewayGql' },
  },
  dataResolver: (data) => {
    return {
      data: data?.DescriptionPage?.querySpecializationPage ?? undefined,
    };
  },
});

export const getDegreeQueryConfig = (slug: string): EntityQueryConfigs<'degree'> => ({
  query: getDegreesBySlug,
  queryOptions: {
    skip: !slug,
    variables: { slug },
    context: { clientName: 'gatewayGql' },
  },
  dataResolver: (data) => {
    const degreeMetadata = data?.PremiumProductCollections?.getBySlug ?? undefined;

    return {
      data: degreeMetadata,
    };
  },
});

export const getMastertrackQueryConfig = (slug: string): EntityQueryConfigs<'mastertrack'> => ({
  query: MasterTrackEntityQuery,
  queryOptions: {
    skip: !slug,
    variables: { slug },
  },
  dataResolver: (data) => {
    const masterTrackMetadata = data?.CatalogMastertracksV1Resource?.bySlug?.elements?.[0] ?? undefined;

    return {
      data: masterTrackMetadata,
    };
  },
});
